"use client";

import React, { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";

import { useWizard } from "@/app/shared-providers/wizard-context-provider";
import SocialProofChip from "@/components/molecules/quote-section/quote-cover/social-proof-chip/social-proof-chip";
import withApplication from "@/components/with-application";
import {
  OptimizelyExperimentsEnum,
  Quote,
  RenewalPage,
} from "@/config/app-constant";
import { ANNUAL_MULTI_TRIP, APPLICATION, PRODUCT } from "@/constants";
import { updateisLoadingCoverState } from "@/store/slices/quote/quote.slice";
import { PolicyPackagePrice } from "@/types/PolicyPackagePriceType";
import defaqtoStyles from "@/utils/defaqto-brand-colors.utils";
import headerClassNames from "@/utils/get-brand-quote-cover-top-classes.utils";

import styles from "./cover-level-selection-card-top.module.css";
import Defaqto from "../defaqto/defaqto";

type HeaderClassNamesType = {
  headerClassName: string;
  headerDetaisClassNames: {
    mainTextClassName: string;
    subTextClassName: string;
  };
  topSectionclassName: string;
};

type CoverSingleCardProps = {
  cardTopData?: {
    cardTitle?: string;
    selectBtnInactiveLable?: string;
    selectBtnActiveLable?: string;
    isLoading?: boolean;
  };
  cover: PolicyPackagePrice | undefined;
  defaqtoStarts: 1 | 2 | 3 | 4 | 5;
  id?: number;
  keyValue: string;
  chipTopText?: string;
  isSocialProofChipActive?: boolean;
  referenceId?: string;
  type?: string;
  application?: string;
  onTripTypeToggle?: (tripType: string) => void;
};

// Helper function to format currency
const formatCurrency = (
  value: number | null | undefined,
  currencySymbol: string
): string => {
  return value ? `${currencySymbol}${value.toFixed(2)}` : "";
};

// Helper function to determine if offline price should be shown
const shouldShowOfflinePrice = (
  app?: string,
  showOfflinePrice?: boolean,
  loyaltyDiscountEligible?: boolean
): boolean => {
  return (
    (app === Quote && !!showOfflinePrice) ||
    (app === RenewalPage && !!loyaltyDiscountEligible)
  );
};

// Subcomponent for offline price display
function OfflinePrice({
  value,
  className,
  currencySymbol,
  isHidden = false,
}: Readonly<{
  value: number;
  className: string;
  currencySymbol: string;
  isHidden?: boolean;
}>) {
  // Hide strikethrough price if flag is enabled
  if (isHidden) {
    return null;
  }

  return (
    <span
      className={`line-through text-2xs leading-[24px] font-normal ${className}`}
    >
      {formatCurrency(value, currencySymbol)}
    </span>
  );
}

// Subcomponent for Annual Multi-Trip pricing display
function AnnualMultiTripPricing({
  cover,
  headerDetailsClassNames,
  currencySymbol,
  application,
  onShowOfflinePrice,
}: Readonly<{
  cover: PolicyPackagePrice;
  headerDetailsClassNames: {
    mainTextClassName: string;
    subTextClassName: string;
  };
  currencySymbol: string;
  application?: string;
  onShowOfflinePrice: (price: number) => React.ReactElement | null;
}>) {
  const totalPrice = formatCurrency(
    cover.onlinePresentationTotalWithDiscount ?? cover.total,
    currencySymbol
  );

  const renderOfflinePrice = () => {
    if (!cover.loyaltyDiscountEligible || application !== RenewalPage) {
      return null;
    }

    const shouldShow = shouldShowOfflinePrice(
      application,
      cover.showOfflinePrice,
      cover.loyaltyDiscountEligible
    );

    if (!shouldShow) {
      return null;
    }

    return (
      <p
        className={`whitespace-nowrap text-[16px] font-[400] leading-[24px] ${
          cover.showOfflinePrice
            ? "text-dark-text-functional-subtle"
            : headerDetailsClassNames.subTextClassName
        }`}
      >
        &nbsp;
        {onShowOfflinePrice(
          cover.finalRetailENBPrice || cover.offlineTotal || 0
        )}
      </p>
    );
  };

  const monthlyPaymentLabel = `Monthly payment: ${formatCurrency(
    cover.collectionAmount,
    currencySymbol
  )} per month`;

  return (
    <div className="flex flex-wrap justify-between items-center">
      <div
        className={`flex flex-col items-center justify-center ${
          renderOfflinePrice() ? "h-[54px]" : ""
        } `}
      >
        <p
          className={`whitespace-nowrap ${headerDetailsClassNames.mainTextClassName} text-[23px] font-[700] leading-[26px]`}
          aria-label={`Total price: ${totalPrice}`}
        >
          {totalPrice}
        </p>
        {renderOfflinePrice()}
      </div>
      <div className="flex justify-center items-center w-[44px]">
        <p
          className={`whitespace-nowrap ${headerDetailsClassNames.mainTextClassName} text-[18px] font-[400] leading-[27px]`}
        >
          or
        </p>
      </div>
      <div className="flex flex-col items-center justify-center h-[52px]">
        <p
          className={`whitespace-nowrap ${headerDetailsClassNames.mainTextClassName} text-[23px] font-[700] leading-[26px]`}
          aria-label={monthlyPaymentLabel}
        >
          {formatCurrency(cover.collectionAmount, currencySymbol)}
          <span
            className={`${headerDetailsClassNames.mainTextClassName} font-[600] text-[16px] leading-[24px]`}
          >
            &nbsp;a month
          </span>
        </p>
        <p
          className={`whitespace-nowrap ${headerDetailsClassNames.subTextClassName} font-[400] text-[16px] leading-[24px]`}
        >
          ({formatCurrency(cover.totalCost, currencySymbol)} total)
        </p>
      </div>
    </div>
  );
}

// Subcomponent for Single Payment pricing display
function SinglePaymentPricing({
  cover,
  headerDetailsClassNames,
  currencySymbol,
  application,
}: Readonly<{
  cover: PolicyPackagePrice;
  headerDetailsClassNames: {
    mainTextClassName: string;
    subTextClassName: string;
  };
  currencySymbol: string;
  application?: string;
}>) {
  const totalPrice = formatCurrency(
    cover.onlinePresentationTotalWithDiscount ?? cover.total,
    currencySymbol
  );

  const renderOfflinePrice = () => {
    if (!cover.loyaltyDiscountEligible || application !== RenewalPage) {
      return null;
    }

    const shouldShow = shouldShowOfflinePrice(
      application,
      cover.showOfflinePrice,
      cover.loyaltyDiscountEligible
    );

    if (!shouldShow) {
      return null;
    }

    const offlinePriceValue =
      cover.finalRetailENBPrice || cover.offlineTotal || 0;

    return (
      <p>
        <OfflinePrice
          value={offlinePriceValue}
          className={headerDetailsClassNames.subTextClassName}
          currencySymbol={currencySymbol}
        />
      </p>
    );
  };

  return (
    <div
      className={`${
        renderOfflinePrice() ? "h-[54px]" : ""
      } flex flex-col items-center justify-center`}
    >
      <p
        className={`${headerDetailsClassNames.mainTextClassName} text-[23px] font-[700] leading-[26px]`}
        data-testid={`price-card-${cover.policyType}-${cover.policyPackage}`}
        aria-label={`Price: ${totalPrice}`}
      >
        {totalPrice}
      </p>
      {renderOfflinePrice()}
    </div>
  );
}

function CoverSingleCardTopAb({
  cardTopData = {},
  cover,
  defaqtoStarts = 1,
  keyValue,
  isSocialProofChipActive = false,
  chipTopText,
  referenceId,
  type = "",
  application,
}: Readonly<Partial<CoverSingleCardProps>>) {
  const dispatch = useDispatch();
  const { current } = useWizard();

  const isAbVariantChipPosition = useMemo(() => {
    if (
      typeof window !== "undefined" &&
      localStorage.getItem(
        OptimizelyExperimentsEnum.RENEWAL_COVER_LEVEL_CHIP
      ) &&
      APPLICATION === "renewal" &&
      PRODUCT === "staysure" &&
      isSocialProofChipActive &&
      chipTopText
    ) {
      return localStorage.getItem(
        OptimizelyExperimentsEnum.RENEWAL_COVER_LEVEL_CHIP
      );
    }
    return false;
  }, [chipTopText, isSocialProofChipActive]);

  // Memoize computed values for performance
  const headerClassNamesList: HeaderClassNamesType = useMemo(
    () => headerClassNames[keyValue as keyof typeof headerClassNames],
    [keyValue]
  );

  const { headerDetaisClassNames, topSectionclassName } = headerClassNamesList;

  const currencySymbol = useMemo(
    () => (current.brandSpecificParams?.currencySymbol as string) ?? "",
    [current.brandSpecificParams?.currencySymbol]
  );

  const isHideStrikethroughAndExclusivePrice =
    current.uiElementControls?.isHideStrikethroughAndExclusivePrice;

  const showOfflinePriceComponent = (
    value: number
  ): React.ReactElement | null => {
    return (
      <OfflinePrice
        value={value}
        className={headerDetaisClassNames.subTextClassName}
        currencySymbol={currencySymbol}
        isHidden={isHideStrikethroughAndExclusivePrice}
      />
    );
  };

  const renderPricingSection = (): React.ReactElement | null => {
    if (!referenceId || !cover) {
      return null;
    }

    const isAnnualMultiTrip =
      type === ANNUAL_MULTI_TRIP && cover.collectionAmount != null;

    if (isAnnualMultiTrip) {
      return (
        <AnnualMultiTripPricing
          cover={cover}
          headerDetailsClassNames={headerDetaisClassNames}
          currencySymbol={currencySymbol}
          application={application}
          onShowOfflinePrice={showOfflinePriceComponent}
        />
      );
    }

    return (
      <SinglePaymentPricing
        cover={cover}
        headerDetailsClassNames={headerDetaisClassNames}
        currencySymbol={currencySymbol}
        application={application}
      />
    );
  };

  // Optimized useEffect
  useEffect(() => {
    dispatch(updateisLoadingCoverState(!!cardTopData.isLoading));
  }, [cardTopData.isLoading, dispatch]);

  return (
    <div className="relative">
      {isAbVariantChipPosition &&
        isAbVariantChipPosition === "top" &&
        chipTopText && (
          <SocialProofChip
            chipLabel={chipTopText}
            position="top"
            cardTitle={cardTopData?.cardTitle ?? ""}
          />
        )}
      <div
        className={`${topSectionclassName} ${styles.backgroundColor} pt-8 pb-4 px-4  md:p-4 h-fit sm:h-[183px] md:h-[180px] w-full flex flex-col items-center justify-center rounded-t-[16px]`}
      >
        <div
          data-testid="CoveringCard-top-testidd"
          className="flex flex-col gap-3 justify-center"
        >
          <div className="px-spacing-m flex flex-col gap-3 md:gap-2  items-center justify-center">
            <div className="text-dark-text-title font-header-font md:text-[31px] text-[26px] font-[400] md:leading-[34px] leading-[30px]">
              <p>{cardTopData?.cardTitle ?? ""}</p>
            </div>
            {current.brandSpecificParams?.isDisplayDefaqtoStarts && (
              <div
                className={`px-spacing-xs py-spacing-xxs ${defaqtoStyles.background} ${styles.defaqtoBg} w-[185px] h-[27px] flex justify-center items-center rounded-[4px]`}
              >
                <Defaqto stars={defaqtoStarts} />
              </div>
            )}
          </div>
          <div className="flex pt-0 md:pt-1 justify-center items-center">
            <div className="flex flex-col justify-center items-center px-6 sm:px-0 xl:px-6 font-body-font">
              {renderPricingSection()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default withApplication(CoverSingleCardTopAb);
